import { apiClient, PaginatedResponse, QueryParams } from './apiClient';

export interface ChannelAutomationRule {
  id?: string;
  displayOrder: number;
  enabled: boolean;
  matchRegex: string;
  playlistGroup?: string | null;
  tvChannelId: string;
  flussonicStreamName: string;
  channelTitleTemplate?: string | null;
  appendMatchSuffix: boolean;
  notes?: string | null;
}

export interface ChannelAutomation extends Record<string, unknown> {
  id: string;
  name: string;
  enabled: boolean;
  iptvProviderId: string;
  flussonicServerId: string;
  cronExpression: string;
  timezone: string;
  notes?: string | null;
  lastRunAt?: string | null;
  lastRunStatus?: string | null;
  nextRunAt?: string | null;
  rules: ChannelAutomationRule[];
}

export interface ChannelAutomationRunItem {
  id: string;
  ruleId?: string | null;
  status: string;
  matchedPlaylistTitle?: string | null;
  matchedPlaylistGroup?: string | null;
  flussonicStreamName?: string | null;
  tvChannelId?: string | null;
  channelTitle?: string | null;
  actionSummary?: string | null;
  errorMessage?: string | null;
}

export interface ChannelAutomationRun {
  id: string;
  automationId: string;
  status: string;
  triggerType: string;
  startedAt: string;
  finishedAt?: string | null;
  matchedCount: number;
  missingCount: number;
  flussonicUpdatedCount: number;
  channelsUpdatedCount: number;
  errorSummary?: string | null;
  items: ChannelAutomationRunItem[];
}

export interface ChannelAutomationPayload {
  name: string;
  enabled: boolean;
  iptvProviderId: string;
  flussonicServerId: string;
  cronExpression: string;
  timezone: string;
  notes?: string;
  rules: ChannelAutomationRule[];
}

export interface ChannelAutomationRuleTestRequest {
  iptvProviderId: string;
  matchRegex: string;
  playlistGroup?: string | null;
  channelTitleTemplate?: string | null;
  appendMatchSuffix: boolean;
}

export interface ChannelAutomationQueryParams extends QueryParams {
  enabled?: boolean;
}

export interface ChannelAutomationRuleTestResponse {
  matched: boolean;
  totalItems: number;
  matchedPlaylistTitle?: string | null;
  matchedPlaylistGroup?: string | null;
  matchedPlaylistLogo?: string | null;
  matchedPlaylistUrl?: string | null;
  matchedPrefix?: string | null;
  suffix?: string | null;
  channelTitle?: string | null;
  message: string;
}

export const channelAutomationService = {
  async getAutomations(params: ChannelAutomationQueryParams = {}): Promise<PaginatedResponse<ChannelAutomation>> {
    return apiClient.getPaginated<ChannelAutomation>('/channel-automations', params);
  },

  async createAutomation(request: ChannelAutomationPayload): Promise<{ success: boolean; automation: ChannelAutomation }> {
    return apiClient.post('/channel-automations', request);
  },

  async testRule(request: ChannelAutomationRuleTestRequest): Promise<ChannelAutomationRuleTestResponse> {
    return apiClient.post('/channel-automations/test-rule', request);
  },

  async updateAutomation(id: string, request: ChannelAutomationPayload): Promise<{ success: boolean; automation: ChannelAutomation }> {
    return apiClient.put(`/channel-automations/${id}`, request);
  },

  async deleteAutomation(id: string): Promise<{ success: boolean; message: string }> {
    return apiClient.delete(`/channel-automations/${id}`);
  },

  async runAutomation(id: string): Promise<ChannelAutomationRun> {
    return apiClient.post(`/channel-automations/${id}/run`);
  },

  async getRuns(id: string): Promise<{ success: boolean; runs: ChannelAutomationRun[] }> {
    return apiClient.get(`/channel-automations/${id}/runs`);
  },
};
